// Multi-step Stock Take Creation Page
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigation } from "@/components/layout/app-layout";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, ArrowRight, Save, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { StockTakeSetupStep } from "@/components/stock-take-create/setup-step";
import { StockTakeInProgressStep } from "@/components/stock-take-create/in-progress-step";
import { StockTakeReviewStep } from "@/components/stock-take-create/review-step";
import { StockTakeStepIndicator } from "@/components/stock-take-create/step-indicator";
import { mockStockTakes, StockTake, StockTakeStatus } from "@/data/mockStockTakes";

export interface StockTakeFormData {
  date: Date;
  type: string;
  description: string;
  selectedItems: SelectedStockItem[];
  countedQuantities: Record<string, number>;
  recountFlags: Record<string, boolean>;
}

export interface SelectedStockItem {
  code: string;
  description: string;
  category: string;
  location: string;
  expectedQuantity: number;
  unitPrice: number;
}

type Step = "setup" | "in-progress" | "review";

interface StockTakeCreatePageProps {
  stockTakeId?: string;
}

export function StockTakeCreatePage({ stockTakeId }: StockTakeCreatePageProps = {}) {
  const { navigateTo, pageParams } = useNavigation();
  const [isLoading, setIsLoading] = useState(!!stockTakeId);
  const [stockTakeReference, setStockTakeReference] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>("setup");
  const [formData, setFormData] = useState<StockTakeFormData>({
    date: new Date(),
    type: "",
    description: "",
    selectedItems: [],
    countedQuantities: {},
    recountFlags: {},
  });

  // Dialog states
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    action: "next" | "back" | "save" | "complete" | "exit";
    targetStep?: Step;
  } | null>(null);

  // Load existing stock take if ID is provided
  useEffect(() => {
    if (stockTakeId) {
      loadStockTake(stockTakeId);
    }
  }, [stockTakeId]);

  // Handle pre-populated items from Stock Items page
  useEffect(() => {
    if (pageParams?.prepopulatedItems && Array.isArray(pageParams.prepopulatedItems)) {
      const prepopulatedItems = pageParams.prepopulatedItems as any[];
      
      // Convert to SelectedStockItem format
      const selectedItems: SelectedStockItem[] = prepopulatedItems.map((item) => ({
        code: item.code,
        description: item.description,
        category: item.category || "Uncategorized",
        location: item.location,
        expectedQuantity: item.expectedQuantity || 0,
        unitPrice: item.unitPrice || 0,
      }));

      // Update form data with pre-populated items
      setFormData((prev) => ({
        ...prev,
        selectedItems,
      }));

      // Show success toast
      toast.success(`${selectedItems.length} item${selectedItems.length !== 1 ? "s" : ""} pre-populated from Stock Items`);
    }
  }, [pageParams]);

  // Load stock take data
  const loadStockTake = (id: string) => {
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const stockTake = mockStockTakes.find((st) => st.id === id);
      
      if (!stockTake) {
        toast.error("Stock take not found");
        navigateTo("stock-takes");
        return;
      }

      // Check if stock take is complete
      if (stockTake.status === "Complete") {
        toast.error("This stock take is already complete");
        navigateTo("stock-takes");
        return;
      }

      // Convert stock take data to form data
      const selectedItems: SelectedStockItem[] = stockTake.items.map((item) => ({
        code: item.stockCode,
        description: item.stockDescription,
        category: "General", // Mock category
        location: item.location,
        expectedQuantity: item.systemQuantity,
        unitPrice: 100, // Mock price
      }));

      const countedQuantities: Record<string, number> = {};
      const recountFlags: Record<string, boolean> = {};
      
      stockTake.items.forEach((item) => {
        if (item.countedQuantity !== null) {
          countedQuantities[item.stockCode] = item.countedQuantity;
        }
        // Mark items with large variances for recount
        if (item.variance !== null && Math.abs(item.variance) > 10) {
          recountFlags[item.stockCode] = true;
        }
      });

      setFormData({
        date: stockTake.createdAt,
        type: "Full Count", // Mock type
        description: stockTake.description,
        selectedItems,
        countedQuantities,
        recountFlags,
      });

      setStockTakeReference(stockTake.reference);
      setIsEditMode(true);

      // Determine starting step based on status
      const step = getStepFromStatus(stockTake.status);
      setCurrentStep(step);

      setIsLoading(false);
      toast.success(`Loaded stock take ${stockTake.reference}`);
    }, 500);
  };

  // Get step from status
  const getStepFromStatus = (status: StockTakeStatus): Step => {
    switch (status) {
      case "Started":
        return "setup";
      case "In Progress":
        return "in-progress";
      case "Review":
        return "review";
      default:
        return "setup";
    }
  };

  // Handle navigation with confirmation
  const handleNavigationRequest = (
    action: "next" | "back" | "save" | "complete" | "exit",
    targetStep?: Step
  ) => {
    setPendingNavigation({ action, targetStep });
    setShowNavigationDialog(true);
  };

  // Confirm navigation
  const confirmNavigation = () => {
    if (!pendingNavigation) return;

    const { action, targetStep } = pendingNavigation;

    switch (action) {
      case "next":
        if (targetStep) {
          setCurrentStep(targetStep);
          toast.success(`Moved to ${getStepLabel(targetStep)} step`);
        }
        break;
      case "back":
        if (targetStep) {
          setCurrentStep(targetStep);
          toast.info(`Returned to ${getStepLabel(targetStep)} step`);
        }
        break;
      case "save":
        handleSaveAndClose();
        break;
      case "complete":
        handleComplete();
        break;
      case "exit":
        navigateTo("stock-takes");
        break;
    }

    setShowNavigationDialog(false);
    setPendingNavigation(null);
  };

  // Cancel navigation
  const cancelNavigation = () => {
    setShowNavigationDialog(false);
    setPendingNavigation(null);
  };

  // Get step label
  const getStepLabel = (step: Step): string => {
    switch (step) {
      case "setup":
        return "Setup";
      case "in-progress":
        return "In Progress";
      case "review":
        return "Review";
      default:
        return "";
    }
  };

  // Get confirmation message
  const getConfirmationMessage = (): string => {
    if (!pendingNavigation) return "";

    const { action, targetStep } = pendingNavigation;

    switch (action) {
      case "next":
        return `Are you sure you want to proceed to the ${getStepLabel(
          targetStep!
        )} step? Your current progress will be saved.`;
      case "back":
        return `Are you sure you want to go back to the ${getStepLabel(
          targetStep!
        )} step? Any changes will be preserved.`;
      case "save":
        return "Are you sure you want to save and close? You can resume this stock take later.";
      case "complete":
        return "Are you sure you want to complete this stock take? This action cannot be undone.";
      case "exit":
        return "Are you sure you want to exit? Any unsaved changes will be lost.";
      default:
        return "";
    }
  };

  // Validate current step
  const validateStep = (): boolean => {
    switch (currentStep) {
      case "setup":
        if (!formData.date) {
          toast.error("Please select a date");
          return false;
        }
        if (!formData.type) {
          toast.error("Please select a type");
          return false;
        }
        if (formData.selectedItems.length === 0) {
          if (formData.type === "Location Audit") {
            toast.error("Please select a location to include items");
          } else {
            toast.error("Please select at least one stock item");
          }
          return false;
        }
        return true;

      case "in-progress":
        const allCounted = formData.selectedItems.every(
          (item) => formData.countedQuantities[item.code] !== undefined
        );
        if (!allCounted) {
          toast.error("Please enter counted quantities for all items");
          return false;
        }
        return true;

      case "review":
        return true;

      default:
        return false;
    }
  };

  // Handle next step
  const handleNext = () => {
    if (!validateStep()) return;

    let nextStep: Step | null = null;

    switch (currentStep) {
      case "setup":
        nextStep = "in-progress";
        break;
      case "in-progress":
        nextStep = "review";
        break;
    }

    if (nextStep) {
      handleNavigationRequest("next", nextStep);
    }
  };

  // Handle back step
  const handleBack = () => {
    let prevStep: Step | null = null;

    switch (currentStep) {
      case "in-progress":
        prevStep = "setup";
        break;
      case "review":
        prevStep = "in-progress";
        break;
    }

    if (prevStep) {
      handleNavigationRequest("back", prevStep);
    }
  };

  // Handle save and close
  const handleSaveAndClose = () => {
    // In a real app, this would save to backend
    console.log("Saving stock take:", formData);
    toast.success("Stock take saved successfully");
    navigateTo("stock-takes");
  };

  // Handle complete
  const handleComplete = () => {
    // In a real app, this would finalise the stock take
    console.log("Completing stock take:", formData);
    toast.success("Stock take completed successfully");
    navigateTo("stock-takes");
  };

  // Update form data
  const updateFormData = (updates: Partial<StockTakeFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case "setup":
        return (
          <StockTakeSetupStep formData={formData} updateFormData={updateFormData} />
        );
      case "in-progress":
        return (
          <StockTakeInProgressStep
            formData={formData}
            updateFormData={updateFormData}
          />
        );
      case "review":
        return (
          <StockTakeReviewStep formData={formData} updateFormData={updateFormData} />
        );
      default:
        return null;
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading stock take...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleNavigationRequest("exit")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Stock Takes
              </Button>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="text-2xl font-bold">
                  {isEditMode ? `Edit Stock Take: ${stockTakeReference}` : "Create Stock Take"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isEditMode
                    ? "Continue working on this stock take"
                    : "Follow the steps to create a new stock take"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-6">
          <StockTakeStepIndicator currentStep={currentStep} />
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <Card className="p-8">{renderStep()}</Card>
      </div>

      {/* Footer Actions */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-card shadow-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {currentStep !== "setup" && (
                <Button variant="outline" onClick={handleBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => handleNavigationRequest("save")}
              >
                <Save className="h-4 w-4 mr-2" />
                Save & Close
              </Button>

              {currentStep === "review" ? (
                <Button onClick={() => handleNavigationRequest("complete")}>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Complete
                </Button>
              ) : (
                <Button onClick={handleNext}>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showNavigationDialog} onOpenChange={setShowNavigationDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Action</AlertDialogTitle>
            <AlertDialogDescription>{getConfirmationMessage()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNavigation}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmNavigation}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
